// Patient feedback (docs/plans/phase3-portal-feedback-kiswahili.md §5, §6): rating words,
// when a visit can still be rated, and the weekly trend the staff inbox shows. Provisional
// owner decisions (plan P9).

import { addDays, eatDayOf, formatIsoDay, type CalendarDay } from '../time.ts';

export const FEEDBACK_POLICY = {
  /** The portal asks about visits that ended in the last 30 days. */
  portalWindowDays: 30,
  /** An SMS feedback link lasts 7 days. */
  linkValidDays: 7,
  /** Ratings at or below this count as low in the trend. */
  lowRatingMax: 2,
} as const;

const RATING_WORDS = ['Very poor', 'Poor', 'Fair', 'Good', 'Very good'] as const;

/** The words beside each rating, so a number is never the only signal. */
export function feedbackRatingWord(rating: number): string {
  const word = RATING_WORDS[rating - 1];
  if (word === undefined) throw new RangeError('A rating is 1 to 5');
  return word;
}

/** Whether a visit that ended at `endedAt` can still be rated at `now`. */
export function feedbackWindowOpen(endedAt: Date | null, now: Date): boolean {
  if (endedAt === null) return false;
  const age = now.getTime() - endedAt.getTime();
  return age >= 0 && age <= FEEDBACK_POLICY.portalWindowDays * 24 * 60 * 60_000;
}

export interface FeedbackWeek {
  weekStart: string;
  count: number;
  average: number | null;
  low: number;
}

/** Monday of the East Africa week holding `day`. */
function mondayOf(day: CalendarDay): CalendarDay {
  const weekday = new Date(Date.UTC(day.year, day.month - 1, day.day)).getUTCDay();
  return addDays(day, -((weekday + 6) % 7));
}

/**
 * Count, average (one decimal) and low ratings for each of the last `weeks` weeks, oldest
 * first, the last being the week holding `now`. Weeks start on Monday, East Africa Time.
 */
export function summariseFeedbackWeeks(
  rows: readonly { submittedAt: Date; rating: number }[],
  weeks: number,
  now: Date,
): FeedbackWeek[] {
  const thisWeek = mondayOf(eatDayOf(now));
  const starts = Array.from({ length: weeks }, (_, i) =>
    formatIsoDay(addDays(thisWeek, -7 * (weeks - 1 - i))),
  );
  const buckets = new Map(starts.map((s) => [s, { sum: 0, count: 0, low: 0 }]));
  for (const row of rows) {
    const bucket = buckets.get(formatIsoDay(mondayOf(eatDayOf(row.submittedAt))));
    if (bucket === undefined) continue;
    bucket.sum += row.rating;
    bucket.count += 1;
    if (row.rating <= FEEDBACK_POLICY.lowRatingMax) bucket.low += 1;
  }
  return starts.map((weekStart) => {
    const b = buckets.get(weekStart) ?? { sum: 0, count: 0, low: 0 };
    return {
      weekStart,
      count: b.count,
      average: b.count === 0 ? null : Math.round((b.sum / b.count) * 10) / 10,
      low: b.low,
    };
  });
}
